import React, { useRef } from 'react';
import {
  ApplicationView,
  useController,
  StartControllerScope,
} from '@aha-app/mvc';
import styles from './HistoryPanel.module.css';
import toolStyles from '../ToolPanel/ToolPanel.module.css';
import AssistantViewController from '../AssistantView/AssistantViewController';
import HistoryController from './HistoryController';
import useInfiniteScroll from 'components/bookmark_audits_sidebar/InfiniteScroll';
import { AhaSearchField } from 'components/common/reactifyWebComponent';
import Confirm from 'components/common/Confirm';
import { type AssistantChat } from 'components/framework/models';
import creditIconSvg from '../../assets/icons/credit.svg';

const HistoryPanel = () => {
  const assistantController = useController<AssistantViewController>(
    AssistantViewController
  );
  const { size, model } = assistantController.state;
  const historyController = useController<HistoryController>();
  const { hasMore, chats } = historyController.state;
  const spinnerRef = useRef(null);
  const scrollerRef = useRef(null);
  const [confirmDeleteChat, setConfirmDeleteChat] =
    React.useState<AssistantChat | null>(null);

  useInfiniteScroll({
    targetRef: spinnerRef,
    rootRef: scrollerRef,
    hasMore,
    loadMore: () => historyController.actionLoadMoreChats(),
  });

  return (
    <div className={styles.historyPanel}>
      {['full-screen', 'full-page'].includes(size) && (
        <div className={toolStyles.toolPanelHeader}>
          <div className={toolStyles.toolPanelName}>Chat history</div>
          <div className={toolStyles.toolPanelHeaderTools}>
            <aha-button
              kind='icon'
              size='mini'
              onClick={() => assistantController.actionShowToolPanel(null)}
            >
              <i className='far fa-xmark' />
            </aha-button>
          </div>
        </div>
      )}
      <AhaSearchField
        className={styles.historyPanelSearch}
        placeholder='Search'
        throttled={false}
        autofocus
        onInput={event =>
          historyController.actionSearchChats(event.target.value)
        }
      />
      <div className={styles.historyPanelContent} ref={scrollerRef}>
        <div className={styles.historyPanelItems}>
          {chats.map(chat => (
            <aha-menu-item
              class={styles.historyPanelItem}
              selected={chat.id === model.currentChat?.id ? true : undefined}
              key={chat.id}
              onClick={() => void assistantController.actionShowChat(chat)}
            >
              <span className={styles.historyPanelItemTitle}>{chat.name}</span>

              <span className={styles.historyPanelItemActions}>
                <aha-button
                  kind='icon'
                  size='mini'
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    void assistantController.actionShowChat(chat, {
                      openEditName: true,
                    });
                  }}
                >
                  <i className='far fa-pencil' />
                </aha-button>
                <aha-tooltip style={{ '--Tooltip__tooltip--min-width': '0' }}>
                  Edit name
                </aha-tooltip>
                {!!(chat.creditCost && chat.creditCost > 0) && (
                  <>
                    <img
                      className={styles.historyPanelItemCreditIcon}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      src={creditIconSvg}
                    />
                    <aha-tooltip
                      style={{ '--Tooltip__tooltip--min-width': '0' }}
                    >
                      AI credits: {chat.creditCost}
                    </aha-tooltip>
                  </>
                )}
                <aha-button
                  kind='icon'
                  size='mini'
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();

                    setConfirmDeleteChat(chat);
                  }}
                >
                  <i className='far fa-trash' />
                </aha-button>
                <aha-tooltip style={{ '--Tooltip__tooltip--min-width': '0' }}>
                  Delete
                </aha-tooltip>
              </span>
            </aha-menu-item>
          ))}
          {hasMore && <aha-loading-row ref={spinnerRef} kind='list' rows={5} />}
        </div>
        {confirmDeleteChat && (
          <Confirm
            title='Delete chat'
            body='Are you sure you want to delete this chat?'
            onConfirm={() => {
              void historyController.actionDeleteChat(confirmDeleteChat);
              setConfirmDeleteChat(null);
            }}
            onClose={() => setConfirmDeleteChat(null)}
            iconUrl='/exclamation.png'
            showOpenButton={false}
            visible={!!confirmDeleteChat}
            aria-label='delete confirmation'
          />
        )}
      </div>
    </div>
  );
};

export default StartControllerScope(
  HistoryController,
  ApplicationView(HistoryPanel)
);
